import { Dayjs } from 'dayjs'

import { AcceptedDayInput, day, getOnlyDate, getToday, isSameOrAfter } from '~/astra'
import { type StoreState, useStore } from '~/store/store'
import { getBookingIdOrId, getScheduledTime, getTheaterRoom, isSameDayCancellation, isSurgeryInDateRange, sortSurgeriesByCheckInTime } from '~/utils/dips'

import {
    type Include,
    type ScheduledSurgery,
    type UnScheduledSurgery,
    useScheduledSurgeries,
    useScheduledSurgery,
    useUnScheduledSurgeries,
    useUnScheduledSurgery,
} from '../'
import {
    buildSchedulingSurgeryFromParams,
    type CommonScheduledSurgery,
    type CommonUnScheduledSurgery,
    type SchedulingSurgery,
    useCommonScheduledSurgeries,
    useCommonUnScheduledSurgeries,
    useSchedulingBookingIds,
    useSchedulingParamsByBookingId,
    WAITING_LIST_SURGERY_INCLUDE,
    type WaitingListSchedulingSurgery,
} from './surgeries'

export type SurgeryRowSurgery =
    | CommonScheduledSurgery
    | (CommonUnScheduledSurgery & Pick<SchedulingSurgery, 'sagaId' | 'plannedTheatreInTime' | 'scheduled_date' | 'room_code'>)

export type OptimisticScheduledSurgery = ScheduledSurgery | SchedulingSurgery

/** Composite key for indexing surgeries by scheduled day and theater room code. */
export function getDateRoomKey(date: Dayjs | string, roomCode: string): string {
    return `${getOnlyDate(date)}::${roomCode}`
}

export function getSurgeryDateRoomKey(surgery: OptimisticScheduledSurgery): string | undefined {
    const roomCode = getTheaterRoom(surgery.surgeryResources)?.shortName
    if (!roomCode) return undefined

    return getDateRoomKey(getScheduledTime(surgery), roomCode)
}

/** Groups optimistic scheduled + in-flight scheduling surgeries by `getSurgeryDateRoomKey`. */
export function groupSurgeriesByDateAndRoomCode(surgeries: readonly OptimisticScheduledSurgery[]): Map<string, OptimisticScheduledSurgery[]> {
    const byDateRoom = new Map<string, OptimisticScheduledSurgery[]>()

    for (const surgery of surgeries) {
        const key = getSurgeryDateRoomKey(surgery)
        if (!key) continue

        const existing = byDateRoom.get(key) ?? []
        existing.push(surgery)
        byDateRoom.set(key, existing)
    }

    return byDateRoom
}

/** Groups surgeries by booking id for O(1) timeline / card lookups. First match wins, matching legacy `.find()`. */
export function keySurgeriesByBookingId(surgeries: readonly OptimisticScheduledSurgery[]): Map<string, OptimisticScheduledSurgery> {
    const byBookingId = new Map<string, OptimisticScheduledSurgery>()

    for (const surgery of surgeries) {
        const id = getBookingIdOrId(surgery)
        if (!byBookingId.has(id)) {
            byBookingId.set(id, surgery)
        }
    }

    return byBookingId
}

/** Equivalent of the Denormalizer `isNonCancelledSurgery`: completed-as-scheduled or still in-flight. */
const FINISH_CODE_COMPLETED = 'OR'

function isNonCancelledScheduledSurgery(surgery: { finishInformation?: { finishCode?: string | null } | null }): boolean {
    const finishCode = surgery.finishInformation?.finishCode
    return !finishCode || finishCode === FINISH_CODE_COMPLETED
}

/**
 * Hydrates the operational-planner `schedulingSurgeries` slice into Storm-typed
 * `SchedulingSurgery` objects by joining against the supplied unScheduled
 * surgeries list. Drops join misses; callers receive an already-filtered array.
 *
 * Shared between `useFutureOptimisticScheduledSurgeries` and
 * `useOptimisticScheduledSurgeriesByDateRange` so both produce identical
 * scheduling-surgery shapes.
 */
export function buildSchedulingSurgeries<U extends UnScheduledSurgery>(
    allUnScheduled: readonly U[],
    schedulingParams: StoreState['operationalPlanner']['schedulingSurgeries']
) {
    const unScheduledMap = new Map(allUnScheduled.map(u => [u.bookingId, u]))

    return schedulingParams
        .map(params => {
            const unScheduledSurgery = unScheduledMap.get(params.bookingId)
            if (!unScheduledSurgery) return undefined
            return buildSchedulingSurgeryFromParams(unScheduledSurgery, params)
        })
        .filter(s => s !== undefined)
}

type SchedulingSurgeryFrom<U extends UnScheduledSurgery> = ReturnType<typeof buildSchedulingSurgeries<U>>[number]

/**
 * Pure builder shared by `useFutureOptimisticScheduledSurgeries` and waiting-list
 * selectors so hook and reselect paths stay identical.
 */
export function buildFutureOptimisticScheduledSurgeries<S extends ScheduledSurgery, U extends UnScheduledSurgery>(
    allScheduled: readonly S[],
    allUnScheduled: readonly U[],
    schedulingParams: StoreState['operationalPlanner']['schedulingSurgeries']
): (S | SchedulingSurgeryFrom<U>)[] {
    const today = getToday()
    const futureScheduled = allScheduled.filter(s => isSameOrAfter(getScheduledTime(s), today, 'day')).filter(isNonCancelledScheduledSurgery)

    // Match legacy `filterOutAlreadyBooked`: dedupe scheduling surgeries against the
    // *full* unfiltered scheduled set (not just future / non-cancelled ones), keyed
    // by `ScheduledSurgery.id` — the legacy `byBookingId` lookup is `keyBy(_, 'id')`,
    // i.e. `SchedulingSurgery.bookingId` is matched against `ScheduledSurgery.id`.
    const bookedIds = new Set(allScheduled.map(s => s.id))

    const schedulingSurgeries = buildSchedulingSurgeries(allUnScheduled, schedulingParams).filter(
        s => isSameOrAfter(s.plannedTheatreInTime, today, 'day') && !bookedIds.has(s.bookingId)
    )

    return [...futureScheduled, ...schedulingSurgeries]
}

/**
 * Storm-native equivalent of `selectGetOptimisticFutureScheduledSurgeries`:
 * returns all scheduled surgeries from today onwards, plus any in-flight
 * scheduling surgeries (placed locally by the operational planner) that
 * haven't yet shown up in the fetched scheduled set.
 *
 * Consumers can layer further filters on top (e.g. by practitioner, by
 * date sub-range).
 */
export function useFutureOptimisticScheduledSurgeries() {
    const schedulingBookingIds = useSchedulingBookingIds()
    const allScheduled = useScheduledSurgeries()
    const schedulingUnScheduled = useUnScheduledSurgeries({ where: { bookingId: { in: schedulingBookingIds } } })
    const schedulingParams = useStore(state => state.operationalPlanner.schedulingSurgeries)

    return buildFutureOptimisticScheduledSurgeries(allScheduled, schedulingUnScheduled, schedulingParams)
}

/**
 * Storm-native equivalent of `selectGetOptimisticScheduledSurgeries.byDateRange`:
 * returns scheduled surgeries falling inside `[startDate, endDate]` (inclusive),
 * plus any in-flight scheduling surgeries (placed locally by the operational
 * planner) in the same range that haven't yet shown up in the fetched
 * scheduled set.
 *
 * Hook form (vs a plain selector) because the underlying `useScheduledSurgeries`
 * / `useUnScheduledSurgeries` Storm hooks layer shallow-equality re-render
 * guards on top of the entity slices, so this composition only re-runs when
 * one of those slices actually changes — not on every store update.
 *
 * `removeCancelled` defaults to `false`: same call sites
 * (the surgeon-waiting-list prediction) expect cancelled-but-in-range surgeries
 * to count toward the prediction window.
 */
export function useOptimisticScheduledSurgeriesByDateRange(
    startDate: AcceptedDayInput,
    endDate: AcceptedDayInput,
    removeCancelled = false
): (ScheduledSurgery | SchedulingSurgery)[] {
    const inRange = isSurgeryInDateRange(startDate, endDate)

    const schedulingBookingIds = useSchedulingBookingIds()
    const allScheduled = useScheduledSurgeries()
    const bookedSchedulingSurgeries = useScheduledSurgeries({ where: { id: { in: schedulingBookingIds } } })
    const schedulingUnScheduled = useUnScheduledSurgeries({ where: { bookingId: { in: schedulingBookingIds } } })
    const schedulingParams = useStore(state => state.operationalPlanner.schedulingSurgeries)

    const inRangeScheduled = allScheduled.filter(inRange)
    const scheduled = removeCancelled ? inRangeScheduled.filter(isNonCancelledScheduledSurgery) : inRangeScheduled

    // Same dedup predicate as `useFutureOptimisticScheduledSurgeries`, narrowed
    // to the only scheduled ids that can match in-flight scheduling booking ids.
    const bookedIds = new Set(bookedSchedulingSurgeries.map(s => s.id))

    const schedulingSurgeries = buildSchedulingSurgeries(schedulingUnScheduled, schedulingParams).filter(s => inRange(s) && !bookedIds.has(s.bookingId))

    if (schedulingSurgeries.length === 0) return scheduled
    return [...scheduled, ...schedulingSurgeries]
}

/**
 * Matches a surgery scheduled (or in-flight) on `date` in the theater room `roomCode`.
 * Compares the pre-computed `scheduled_date` / `room_code` fields (derived at ingestion
 * for persisted rows, at build time for in-flight rows) instead of range-scanning with dayjs.
 */
function matchesDateAndRoom(date: AcceptedDayInput, roomCode: string) {
    const scheduledDate = getOnlyDate(day(date))
    return (surgery: ScheduledSurgery | SchedulingSurgery): boolean => surgery.scheduled_date === scheduledDate && surgery.room_code === roomCode
}

/**
 * Storm-native equivalent of `selectGetScheduledSurgeries.byDateAndRoomCode`:
 * scheduled surgeries placed in `roomCode` on `date`. `removeCancelled` defaults
 * to `true`, matching the legacy default.
 */
export function useScheduledSurgeriesByDateAndRoomCode(date: AcceptedDayInput, roomCode: string, removeCancelled = true): ScheduledSurgery[] {
    const surgeries = useScheduledSurgeries({ where: { scheduled_date: getOnlyDate(day(date)), room_code: roomCode } })
    return removeCancelled ? surgeries.filter(isNonCancelledScheduledSurgery) : surgeries
}

function useOptimisticScheduledSurgeriesByDateAndRoomCodeInternal<S extends ScheduledSurgery, U extends UnScheduledSurgery>(
    date: AcceptedDayInput,
    roomCode: string,
    removeCancelled: boolean,
    scheduledForRoom: S[],
    schedulingUnScheduled: U[],
    bookedSchedulingSurgeries: ScheduledSurgery[]
): (S | ReturnType<typeof buildSchedulingSurgeries<U>>[number])[] {
    const schedulingParams = useStore(state => state.operationalPlanner.schedulingSurgeries)
    const match = matchesDateAndRoom(date, roomCode)

    const scheduled = removeCancelled ? scheduledForRoom.filter(isNonCancelledScheduledSurgery) : scheduledForRoom

    // See note in `useFutureOptimisticScheduledSurgeries`: dedup keys on
    // `ScheduledSurgery.id`, narrowed to the only scheduled ids that can match
    // in-flight scheduling booking ids.
    const bookedIds = new Set(bookedSchedulingSurgeries.map(s => s.id))

    const schedulingSurgeries = buildSchedulingSurgeries(schedulingUnScheduled, schedulingParams).filter(s => match(s) && !bookedIds.has(s.bookingId))

    if (schedulingSurgeries.length === 0) return scheduled
    return [...scheduled, ...schedulingSurgeries]
}

/**
 * Storm-native equivalent of `selectGetOptimisticScheduledSurgeries.byDateAndRoomCode`:
 * scheduled surgeries placed in `roomCode` on `date`, plus any in-flight scheduling
 * surgeries (placed locally by the operational planner) for the same cell that haven't
 * yet shown up in the fetched scheduled set. `removeCancelled` defaults to `true`,
 * matching the legacy default.
 */
export function useOptimisticScheduledSurgeriesByDateAndRoomCode(
    date: AcceptedDayInput,
    roomCode: string,
    removeCancelled = true
): (ScheduledSurgery | SchedulingSurgery)[] {
    const schedulingBookingIds = useSchedulingBookingIds()
    const scheduledForRoom = useScheduledSurgeries({ where: { scheduled_date: getOnlyDate(day(date)), room_code: roomCode } })
    const schedulingUnScheduled = useUnScheduledSurgeries({ where: { bookingId: { in: schedulingBookingIds } } })
    const bookedSchedulingSurgeries = useScheduledSurgeries({ where: { id: { in: schedulingBookingIds } } })

    return useOptimisticScheduledSurgeriesByDateAndRoomCodeInternal(
        date,
        roomCode,
        removeCancelled,
        scheduledForRoom,
        schedulingUnScheduled,
        bookedSchedulingSurgeries
    )
}

/** Storm-native equivalent of `selectGetOptimisticScheduledSurgeries.byDate`. */
export function useOptimisticScheduledSurgeriesByDate(date: AcceptedDayInput, removeCancelled = true): (ScheduledSurgery | SchedulingSurgery)[] {
    return useOptimisticScheduledSurgeriesByDateRange(date, date, removeCancelled)
}

/** Storm-native equivalent of `selectGetOptimisticScheduledSurgeries.byBookingId`. */
export function useOptimisticScheduledSurgeryByBookingId(bookingId: string): ScheduledSurgery | SchedulingSurgery | undefined
export function useOptimisticScheduledSurgeryByBookingId(
    bookingId: string,
    include: typeof WAITING_LIST_SURGERY_INCLUDE
): Include<ScheduledSurgery, typeof WAITING_LIST_SURGERY_INCLUDE> | WaitingListSchedulingSurgery | undefined
export function useOptimisticScheduledSurgeryByBookingId(
    bookingId: string,
    include?: typeof WAITING_LIST_SURGERY_INCLUDE
): ScheduledSurgery | SchedulingSurgery | Include<ScheduledSurgery, typeof WAITING_LIST_SURGERY_INCLUDE> | WaitingListSchedulingSurgery | undefined {
    const scheduled = useScheduledSurgery({ where: { id: bookingId }, ...(include ? { include } : {}) })
    const schedulingParams = useSchedulingParamsByBookingId(bookingId)
    const unscheduled = useUnScheduledSurgery({ where: { bookingId }, ...(include ? { include } : {}) })

    if (scheduled) return scheduled
    if (!schedulingParams || !unscheduled) return undefined

    return buildSchedulingSurgeryFromParams(unscheduled, schedulingParams)
}

/**
 * Returns scheduled and in-flight surgeries for the given booking ids.
 * Preserves input ordering and drops unresolved ids.
 */
export function useOptimisticScheduledSurgeriesByBookingIds(bookingIds: readonly string[]): OptimisticScheduledSurgery[] {
    const hasBookingIds = bookingIds.length > 0
    const queryIdSet = new Set(bookingIds)

    const allScheduled = useScheduledSurgeries()
    const bookedIds = new Set(allScheduled.map(s => s.id))
    const scheduledMatches = hasBookingIds ? allScheduled.filter(s => queryIdSet.has(s.id)) : []
    const schedulingParams = useStore(state =>
        hasBookingIds ? state.operationalPlanner.schedulingSurgeries.filter(params => queryIdSet.has(params.bookingId)) : []
    )
    const unscheduledMatches = useUnScheduledSurgeries({
        where: { bookingId: { in: hasBookingIds ? [...bookingIds] : [] } },
    })

    const schedulingSurgeries = buildSchedulingSurgeries(unscheduledMatches, schedulingParams).filter(s => !bookedIds.has(s.bookingId))

    if (!hasBookingIds) return []

    const scheduledById = new Map(scheduledMatches.map(s => [s.id, s]))
    const schedulingByBookingId = new Map(schedulingSurgeries.map(s => [s.bookingId, s]))

    const result: OptimisticScheduledSurgery[] = []
    for (const id of bookingIds) {
        const surgery = scheduledById.get(id) ?? schedulingByBookingId.get(id)
        if (surgery) {
            result.push(surgery)
        }
    }

    return result
}

/** Populated variant for surgery-row UIs (booked + in-flight scheduling surgeries), sorted by check-in time. */
export function useOptimisticSurgeryRowsByDateAndRoomCode(date: AcceptedDayInput, roomCode: string, removeCancelled = true): SurgeryRowSurgery[] {
    const schedulingBookingIds = useSchedulingBookingIds()
    const scheduledForRoom = useCommonScheduledSurgeries({ where: { scheduled_date: getOnlyDate(day(date)), room_code: roomCode } })
    const schedulingUnScheduled = useCommonUnScheduledSurgeries({ where: { bookingId: { in: schedulingBookingIds } } })
    const bookedSchedulingSurgeries = useScheduledSurgeries({ where: { id: { in: schedulingBookingIds } } })

    return useOptimisticScheduledSurgeriesByDateAndRoomCodeInternal(
        date,
        roomCode,
        removeCancelled,
        scheduledForRoom,
        schedulingUnScheduled,
        bookedSchedulingSurgeries
    ).toSorted(sortSurgeriesByCheckInTime)
}

/**
 * Storm-native equivalent of `selectGetScheduledSurgeries.byDateRange`.
 * `removeCancelled` defaults to `false`, matching the legacy selector.
 */
export function useScheduledSurgeriesInDateRange(startDate: AcceptedDayInput, endDate: AcceptedDayInput, removeCancelled = false): ScheduledSurgery[] {
    const allScheduled = useScheduledSurgeries()
    const inRange = isSurgeryInDateRange(startDate, endDate)
    const surgeries = allScheduled.filter(inRange)

    return removeCancelled ? surgeries.filter(isNonCancelledScheduledSurgery) : surgeries
}

/** Storm-native equivalent of `selectGetScheduledSurgeries.sameDayCancellationsByDateRange`. */
export function useSameDayCancellationsByDateRange(startDate: AcceptedDayInput, endDate: AcceptedDayInput): ScheduledSurgery[] {
    const allScheduled = useScheduledSurgeries()
    const inRange = isSurgeryInDateRange(startDate, endDate)

    return allScheduled.filter(surgery => inRange(surgery) && isSameDayCancellation(surgery))
}

/**
 * Storm-native equivalent of `selectGetScheduledSurgeries.groupedByDayInDateRange`.
 * Processes all surgeries in a single pass for optimal efficiency.
 */
export function useScheduledSurgeriesGroupedByDayInDateRange(startDate: AcceptedDayInput, endDate: AcceptedDayInput) {
    const allScheduled = useScheduledSurgeries()
    const inRange = isSurgeryInDateRange(startDate, endDate)
    const scheduledByDay: Record<string, ScheduledSurgery[]> = {}
    const cancelledByDay: Record<string, ScheduledSurgery[]> = {}
    let scheduledCount = 0
    let cancelledCount = 0

    for (const surgery of allScheduled) {
        if (!inRange(surgery)) continue

        const dateKey = getOnlyDate(getScheduledTime(surgery))

        if (isNonCancelledScheduledSurgery(surgery)) {
            scheduledByDay[dateKey] ??= []
            scheduledByDay[dateKey].push(surgery)
            scheduledCount++
        } else if (isSameDayCancellation(surgery)) {
            cancelledByDay[dateKey] ??= []
            cancelledByDay[dateKey].push(surgery)
            cancelledCount++
        }
    }

    return { scheduledByDay, cancelledByDay, scheduledCount, cancelledCount }
}
